/**
 * vipUtils — 会员相关纯函数
 *
 * 提供会员状态判断、日期格式化、侧边栏会员信息构建等逻辑，
 * 供 App.js、NutritionAssessment、RecipeCustomization 等页面使用。
 *
 * @module utils/vipUtils
 */

/**
 * 格式化 VIP 过期时间（YY.MM.DD）
 * @param {string|number|Date} value
 * @returns {string}
 */
export const formatVipDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const year = String(date.getFullYear()).slice(-2);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}.${month}.${day}`;
};

/**
 * 获取 VIP 过期时间戳
 * @param {object} vipInfo
 * @returns {string|number}
 */
export const getVipExpireTime = (vipInfo) =>
    vipInfo?.expireTime || vipInfo?.endTime || "";

/**
 * 判断是否为有效 VIP 会员
 * @param {object} vipInfo
 * @returns {boolean}
 */
export const isActiveVipMember = (vipInfo) => {
    if (!vipInfo) return false;
    const status = String(vipInfo.memberStatus || vipInfo.status || "").toUpperCase();
    const level = String(vipInfo.memberLevel || "").toUpperCase();
    return status === "ACTIVE";
};

/**
 * 构建侧边栏所需的会员展示信息
 * @param {object} vipInfo - 原始会员信息
 * @returns {object} 包含 status / expired / expireText
 */
export const buildVipInfoForSidebar = (vipInfo) => {
    const isActive = isActiveVipMember(vipInfo);
    const expireTime = getVipExpireTime(vipInfo);
    let expireText = isActive ? "会员有效期内" : "未开通会员";

    if (expireTime) {
        const formattedDate = formatVipDate(expireTime);
        if (isActive) {
            expireText = `${formattedDate}到期`;
        } else {
            const expiredDays = Math.max(
                0,
                Math.ceil(
                    (Date.now() - new Date(expireTime).getTime()) /
                        (24 * 60 * 60 * 1000),
                ),
            );
            expireText = expiredDays > 0 ? `已过期${expiredDays}天` : "会员已过期";
        }
    }

    return {
        ...(vipInfo || {}),
        status: isActive ? "ACTIVE" : "EXPIRED",
        expired: !isActive,
        expireText,
    };
};
